import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { Text } from '@/components/Text';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEventStore } from '@/stores/useEventStore';
import { MeetingRequest } from '@/types/event';
import { cn } from '@/utils/cn';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaCalendar,
  FaCalendarPlus,
  FaCheck,
  FaClock,
  FaPen,
  FaXmark,
} from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { ReplanMeetingModal } from './ReplanMeetingModal';

export const EventDetailsMeetingRequests = () => {
  const {
    meetingRequests,
    listMeetingRequests,
    acceptMeetingRequest,
    rejectMeetingRequest,
  } = useEventStore();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [replanModalOpen, setReplanModalOpen] = useState(false);
  const [selectedMeetingRequest, setSelectedMeetingRequest] =
    useState<MeetingRequest | null>(null);

  useEffect(() => {
    const fetchMeetingRequests = async () => {
      await listMeetingRequests(Number(id));
    };
    fetchMeetingRequests();
  }, [id, listMeetingRequests]);

  const handleAddToCalendar = (req: MeetingRequest) => {
    const link = document.createElement('a');
    link.href = req.ics_url;
    link.download = 'invite.ics';
    link.click();
  };

  const handleReplanMeeting = (req: MeetingRequest) => {
    setSelectedMeetingRequest(req);
    setReplanModalOpen(true);
  };

  const handleCloseReplanModal = () => {
    setReplanModalOpen(false);
    setSelectedMeetingRequest(null);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'accepted':
        return {
          bg: 'bg-emerald-500',
          text: 'text-white',
          label: t(`eventDetails:meetingRequests.statuses.${status}`),
        };
      case 'waiting':
        return {
          bg: 'bg-amber-500',
          text: 'text-white',
          label: t(`eventDetails:meetingRequests.statuses.${status}`),
        };
      case 'rejected':
        return {
          bg: 'bg-red-500',
          text: 'text-white',
          label: t(`eventDetails:meetingRequests.statuses.${status}`),
        };
      default:
        return {
          bg: 'bg-gray-500',
          text: 'text-white',
          label: status,
        };
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const meetingDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    let dayLabel = '';
    if (meetingDate.getTime() === today.getTime()) {
      dayLabel = "Aujourd'hui";
    } else if (meetingDate.getTime() === tomorrow.getTime()) {
      dayLabel = 'Demain';
    } else {
      dayLabel = date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    }

    const time = date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return { dayLabel, time };
  };

  const renderMeetingRequestCard = (req: MeetingRequest) => {
    const isRecipient = req.recipient.id === user?.id;
    const isReplanner = req.replanned_by === user?.id;
    const showActions =
      (isRecipient || !isReplanner) && req.status === 'waiting';
    const showAddToCalendar = req.status === 'accepted';
    const statusConfig = getStatusConfig(req.status);
    const dateTime = formatDateTime(req.meeting_date);

    return (
      <div
        key={req.id}
        className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md"
      >
        {/* Status Bar */}
        <div className={cn('h-0.5 w-full', statusConfig.bg)} />

        <div className="p-4">
          {/* Header with Status */}
          <div className="mb-4 flex items-start justify-between">
            <div>
              <Text className="mb-1 font-semibold text-gray-900">
                {t('eventDetails:meetingRequests.meetingWith')}
              </Text>
              <Text className="text-xs text-gray-500">
                Demandé le{' '}
                {new Date(req.created_at).toLocaleDateString('fr-FR')}
              </Text>
            </div>
            <span
              className={cn(
                'rounded-full px-2 py-1 text-xs font-medium',
                statusConfig.bg,
                statusConfig.text,
              )}
            >
              {statusConfig.label}
            </span>
          </div>

          {/* Participants */}
          <div className="mb-4">
            <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-center space-x-2">
                <div className="flex size-8 min-h-8 min-w-8 shrink-0 items-center justify-center rounded-full bg-linko-light text-xs font-semibold text-white">
                  {req.sender.firstname.charAt(0)}
                  {req.sender.lastname.charAt(0)}
                </div>
                <div>
                  <Text className="text-sm font-medium text-gray-900">
                    {req.sender.firstname} {req.sender.lastname}
                  </Text>
                  <Text className="text-xs text-gray-500">
                    {t('eventDetails:meetingRequests.sender')}
                  </Text>
                </div>
              </div>

              <FaArrowRight className="mx-2 text-xs text-gray-400" />

              <div className="flex items-center space-x-2">
                <div className="flex size-8 min-h-8 min-w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-xs font-semibold text-white">
                  {req.recipient.firstname.charAt(0)}
                  {req.recipient.lastname.charAt(0)}
                </div>
                <div>
                  <Text className="text-sm font-medium text-gray-900">
                    {req.recipient.firstname} {req.recipient.lastname}
                  </Text>
                  <Text className="text-xs text-gray-500">
                    {t('eventDetails:meetingRequests.recipient')}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          {/* Meeting DateTime */}
          <div className="mb-4">
            <div className="flex items-center space-x-3 rounded-lg border border-purple-100 bg-gradient-to-r from-purple-50 to-blue-50 p-3">
              <div className="flex size-8 min-h-8 min-w-8 shrink-0 items-center justify-center rounded-full bg-purple-100">
                <FaClock className="size-3 text-purple-600" />
              </div>
              <div className="flex-1">
                <Text className="text-sm font-semibold text-gray-900">
                  {dateTime.dayLabel}
                </Text>
                <Text className="text-xs font-medium text-purple-600">
                  {dateTime.time}
                </Text>
              </div>
            </div>
          </div>

          {/* Message */}
          {req.message && (
            <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-3">
              <Text className="mb-1 text-xs font-medium text-linko-light">
                {t('eventDetails:meetingRequests.message')}
              </Text>
              <Text className="line-clamp-2 text-xs italic text-linko-light">
                &ldquo;{req.message}&rdquo;
              </Text>
            </div>
          )}

          {/* Replanner Warning */}
          {req.status === 'waiting' && isReplanner && (
            <div className="mb-4 rounded-lg border border-orange-100 bg-orange-50 p-3">
              <Text className="text-xs font-medium text-orange-700">
                {t('eventDetails:meetingRequests.cannotAcceptOwnReplan')}
              </Text>
            </div>
          )}

          {/* Actions for Waiting Status */}
          {showActions && (
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={() => acceptMeetingRequest(req.id)}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
                >
                  <FaCheck className="size-3" />
                  {t('eventDetails:meetingRequests.accept')}
                </Button>
                <Button
                  onClick={() => rejectMeetingRequest(req.id)}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  <FaXmark className="size-3" />
                  {t('eventDetails:meetingRequests.decline')}
                </Button>
              </div>
              <Button
                onClick={() => handleReplanMeeting(req)}
                className="flex w-full items-center justify-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900"
              >
                <FaPen className="size-3" />
                {t('eventDetails:meetingRequests.requestReplan')}
              </Button>
            </div>
          )}

          {/* Actions for Accepted Status */}
          {showAddToCalendar && (
            <div className="space-y-3">
              <div className="flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 p-2">
                <FaCheck className="mr-1 size-3 text-emerald-600" />
                <Text className="text-sm font-medium text-emerald-800">
                  {t('eventDetails:meetingRequests.meetingAccepted')}
                </Text>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={() => handleAddToCalendar(req)}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-linko-light px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-linko-dark"
                >
                  <FaCalendarPlus className="size-3" />
                  Agenda
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!meetingRequests) return <Loading />;

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <div className="mb-3 flex items-center space-x-3">
          <div className="flex size-10 items-center justify-center rounded-lg bg-gradient-to-br from-linko-light to-linko-dark">
            <FaCalendar className="size-5 text-white" />
          </div>
          <div>
            <Text className="text-xl font-bold text-gray-900">
              {t('eventDetails:meetingRequests.subtitle')}
            </Text>
            <Text className="text-sm text-gray-600">
              {meetingRequests.length}{' '}
              {meetingRequests.length === 1 ? 'demande' : 'demandes'}
            </Text>
          </div>
        </div>
      </div>

      {/* Content */}
      {meetingRequests.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-gray-100">
            <FaCalendar className="size-8 text-gray-400" />
          </div>
          <Text className="mb-2 text-lg font-semibold text-gray-600">
            {t('eventDetails:meetingRequests.noRequests')}
          </Text>
          <Text className="mx-auto max-w-md text-sm text-gray-500">
            {t('eventDetails:meetingRequests.noRequestsDescription')}
          </Text>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {meetingRequests.map(req => renderMeetingRequestCard(req))}
        </div>
      )}

      <ReplanMeetingModal
        isOpen={replanModalOpen}
        onClose={handleCloseReplanModal}
        meetingRequest={selectedMeetingRequest}
      />
    </>
  );
};
